import React, { useRef, useState } from "react";
import { CalendarClock, X } from "lucide-react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { enUS, mk } from "date-fns/locale";
import {
  dayDifference,
  dayDifferenceLabel,
  formatDateTime,
  parseDateTime,
  resolveTypedDateTime,
} from "../utils/logBookDateTime";

registerLocale("en", enUS);
registerLocale("mk", mk);

// Почеток/Крај cell of the Log Book (OSProductivityTable). Stored value: full
// `DD.MM.YYYY HH:mm` (utils/logBookDateTime.ts). Pure helpers live there; this file is the
// react-datepicker wiring only.

// Canonical blue focus accent (CLAUDE.md hard rule — the yellow variant this carried is the
// known accent bug, not a style choice). Shared with the planned-minutes inputs of the table.
export const cellCls =
  "w-full h-6 px-1 border-0 outline-none text-[11px] bg-transparent text-right tabular-nums hover:bg-blue-50 focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50";

interface Props {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  // Narrow RN productivity variant: shows only HH:mm while not being edited (plus a blue
  // `(+1)` day suffix when the date differs from baseDate); on focus the full
  // DD.MM.YYYY HH:mm is shown so typing + Enter (onCommit) works exactly as before.
  compact?: boolean;
  // Reference cell's full value (row.montageStart): the date a time-only entry is placed on,
  // and the day the compact suffix counts from.
  baseDate?: string;
  /** Deliberate Enter commit for the Razmer старт-status preview. */
  onCommit?: () => void;
}

const LogBookDateTimeCell: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  compact = false,
  baseDate,
  onCommit,
}) => {
  const hasValue = Boolean(value);
  const [editing, setEditing] = useState(false);
  // The raw text as typed. react-datepicker keeps it in its own state and only calls
  // onChange once the FULL `dd.MM.yyyy HH:mm` format parses, so a time-only entry (`15:00`)
  // never reaches onChange by itself — we read it back here on Enter and on leaving the cell.
  // Cleared whenever the picker itself delivers a Date (typed full value, calendar or time
  // list click) so a stale typed text can never overwrite a picked value.
  const typedRef = useRef("");
  const showTimeOnly = compact && !editing;
  const daySuffix = showTimeOnly && hasValue ? dayDifferenceLabel(dayDifference(value, baseDate)) : "";

  const pickerChange = (date: Date | null) => {
    typedRef.current = "";
    onChange(date ? formatDateTime(date) : "");
  };

  // Enter: a full date+time OR a time only (date from the row's Почеток монтажа, else this
  // cell's own date, else today — resolveTypedDateTime) is written; with onCommit it is
  // always written and committed, so the production-status preview starts exactly as with a
  // typed full value. Garbage (25:99, half-typed dates) is left to the picker, which drops
  // it on close — nothing is written.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter") return;
    const target = event.target as HTMLInputElement | null;
    const typed = typeof target?.value === "string" ? target.value : "";
    const date = resolveTypedDateTime(typed, baseDate, value);
    if (!date) return;
    typedRef.current = "";
    const next = formatDateTime(date);
    if (onCommit) {
      onChange(next);
      onCommit();
      return;
    }
    if (next !== value) onChange(next);
  };

  // Leaving the cell (blur, Tab, click outside, Escape) with a typed time-only entry still
  // pending: resolve it the same way as Enter, without the commit. Runs from both onBlur and
  // onCalendarClose (the picker fires only one of them depending on how the cell was left),
  // so it is idempotent — the second call finds nothing typed.
  const finishEditing = () => {
    setEditing(false);
    const typed = typedRef.current;
    typedRef.current = "";
    if (!typed) return;
    const date = resolveTypedDateTime(typed, baseDate, value);
    if (!date) return;
    const next = formatDateTime(date);
    if (next !== value) onChange(next);
  };

  const inputCls = [
    cellCls,
    compact ? (daySuffix ? "!pl-0.5 !pr-[15px]" : "!pr-1") : "!pr-8",
    compact ? "group-hover:!pr-5" : "",
    compact && editing ? "relative z-20 !w-[132px] bg-white shadow-md" : "",
    disabled ? "bg-gray-50 cursor-not-allowed text-gray-700" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className="group relative w-full" title={value || undefined}>
      <DatePicker
        selected={parseDateTime(value)}
        onChange={pickerChange}
        onChangeRaw={(event) => {
          const target = event?.target;
          typedRef.current = target instanceof HTMLInputElement ? target.value : "";
        }}
        onKeyDown={handleKeyDown}
        showTimeSelect
        timeIntervals={5}
        timeCaption="Време"
        timeFormat="HH:mm"
        dateFormat={showTimeOnly ? "HH:mm" : "dd.MM.yyyy HH:mm"}
        onFocus={() => setEditing(true)}
        onBlur={finishEditing}
        onCalendarClose={finishEditing}
        wrapperClassName="w-full"
        className={inputCls}
        showPopperArrow={false}
        isClearable={false}
        disabled={disabled}
        locale="mk"
        popperPlacement="bottom-start"
        popperClassName="z-[99999]"
        popperProps={{ strategy: "fixed" }}
        portalId="date-picker-portal"
      />
      {hasValue && !disabled && (
        <button
          type="button"
          aria-label="Исчисти време"
          title="Исчисти"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => pickerChange(null)}
          className={`absolute ${compact ? "right-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100" : "right-5"} top-1/2 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 focus:outline-none focus:ring-1 focus:ring-red-300`}
        >
          <X size={11} strokeWidth={2.5} />
        </button>
      )}
      {!compact && (
        <CalendarClock
          size={13}
          className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-gray-500"
        />
      )}
      {daySuffix && (
        <span className="pointer-events-none absolute right-0.5 top-1/2 -translate-y-1/2 whitespace-nowrap text-[8px] font-semibold leading-none text-blue-600 group-hover:invisible">
          {daySuffix}
        </span>
      )}
    </div>
  );
};

export default LogBookDateTimeCell;
